import React, { useState } from "react";
import { motion } from "framer-motion";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import servicesData from "./servicesData";
import ServicesPage from "./ServicesPage";

const Container = ({ children }) => (
  <div className="max-w-7xl mx-auto px-6">{children}</div>
);

const Button = ({ children, primary }) => (
  <button
    className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${primary
      ? "bg-black text-white hover:bg-gray-800 shadow-lg"
      : "border border-black text-black hover:bg-black hover:text-white"
      }`}
  >
    {children}
  </button>
);

function Navbar() {
  const navigate = useNavigate();

  const handleHomeClick = (e) => {
    e.preventDefault();
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="fixed top-0 w-full bg-white/80 backdrop-blur-md shadow-sm z-50">
      <Container>
        <div className="flex justify-between items-center py-4">
          <a href="/" onClick={handleHomeClick} className="flex items-center gap-3">
            <img
              src="/images/team/small_logo.png"
              alt="Swastik Advisors Logo"
              className="h-10 w-10"
            />
            <h1 className="text-xl font-bold text-black">Swastik Advisors</h1>
          </a>
          <div className="hidden md:flex gap-6 items-center">
            <a href="/#services" className="text-gray-600 hover:text-black transition-colors">Services</a>
            <a href="/#about" className="text-gray-600 hover:text-black transition-colors">About</a>
            <a href="/#consultation" className="text-gray-600 hover:text-black transition-colors">Contact</a>
            <a href="/#consultation">
              <Button primary>Book Consultation</Button>
            </a>
          </div>
        </div>
      </Container>
    </div>
  );
}

const Hero = () => (
  <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 via-white to-slate-100">
    <Container>
      <div className="grid md:grid-cols-2 gap-20 items-center">
        <div>
          <h1 className="text-6xl md:text-7xl font-bold tracking-tight mb-6 max-w-xl">
            Strategic Financial Advisory for 
            <span className="block text-slate-600">
              Modern Businesses
            </span>
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-lg leading-relaxed">
            We are your trusted partners in growth—leveraging technology,
            expertise, and innovation to deliver transformative financial
            solutions.
          </p>
          <div className="flex gap-4">
            <a href="#consultation">
              <Button primary>Book a Consultation</Button>
            </a>
            <a href="#services">
              <Button>Explore Services</Button>
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-gray-500">
            <span>Audit</span>
            <span>•</span>
            <span>Taxation</span>
            <span>•</span>
            <span>Compliance</span>
            <span>•</span>
            <span>Financial Advisory</span>
          </div>
        </div>
        <motion.div
          //initial={{ opacity: 0, y: 40 }}
          initial={{ opacity: 0, x: -50 }}
          //animate={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8}}
          whileHover={{ scale: 1.03 }}
          className="relative rounded-3xl shadow-2xl overflow-hidden border border-gray-100"
        >
          <div className="absolute -inset-4 bg-slate-200/30 blur-3xl rounded-full" />
          <img
            src="/images/team/swasthik_banner.PNG"
            alt="Swastik Advisors Banner"
            className="w-full h-full object-cover"
          />
        </motion.div>
      </div>
    </Container>
  </section>
);

const About = () => (
  <section id="about" className="py-20">
    <Container>
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-4xl font-bold text-slate-900 mb-2">Modern Advisory. Timeless Trust</h2>
          <p className="text-slate-600 mb-6">Strategic financial advisory tailored for ambitious businesses.</p>
          <p className="text-gray-600">
            Swastik Advisors is a trusted partner to businesses seeking clarity, growth, and long-term success. We combine deep financial expertise, industry knowledge, and technology-driven insights to help organizations strengthen decision-making, manage risk, and unlock new opportunities.          </p>
          <p className="text-gray-600 mt-4">
            By leveraging advanced analytical tools, data-driven methodologies, and extensive financial knowledge, we deliver practical, forward-looking solutions that create long-term value for our clients.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          {
            title: "Innovation First",
            desc: "Leveraging modern technology to deliver smarter financial solutions."
          },
          {
            title: "Client Partnership",
            desc: "Building long-term relationships based on trust and transparency."
          },
          {
            title: "Excellence & Integrity",
            desc: "Maintaining the highest standards of professionalism and ethics."
          },
          {
            title: "Technology-Driven",
            desc: "Using modern tools and analytics to create better outcomes."
          },
          {
            title: "Continuous Learning",
            desc: "Staying ahead of industry trends and regulatory developments."
          }
        ].map((item, i) => (
          <div
            key={i}
            className="
              bg-white
              p-6
              rounded-2xl
              border border-slate-100
              shadow-sm
              hover:border-slate-300
              hover:shadow-xl
              hover:-translate-y-1
              transition-all
              duration-300
            "
          >

            <h3 className="font-semibold text-xl mb-3 text-slate-900">
              {item.title}
            </h3>

            <p className="text-sm text-slate-600 leading-relaxed">
              {item.desc}
            </p>
          </div>
        ))}
          </div>
      </div>
    </Container>
  </section>
);

const Services = () => {
  return (
    <section id="services" className="py-20 bg-gray-50 scroll-mt-12">
      <Container>
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Enterprise Services</h2>
        <p className="text-gray-500 text-center mb-12 max-w-2xl mx-auto">
          Comprehensive financial and business advisory solutions tailored for your growth.
        </p>
        <div className="grid md:grid-cols-3 gap-8">
          {servicesData.map((service) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{
                y: -10,
                scale: 1.03
              }}
              transition={{
                duration: 0.3
              }}
              className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-2xl transition-all duration-300 cursor pointer group flex flex-col"
            >
              <div className="w-12 h-12 rounded-xl bg-gray-100 text-black flex items-center justify-center mb-4 group-hover:bg-black group-hover:text-white transition-colors duration-300">
                {service.icon}
              </div>
              <h3 className="font-bold text-xl mb-2 text-black">{service.title}</h3>
              <p className="text-sm text-gray-500 mb-5 flex-grow">
                {service.shortDesc}
              </p>
              <Link
                to={`/services/${service.id}`}
                className="inline-flex items-center gap-1.5 text-black font-medium text-sm hover:gap-3 transition-all duration-300"
              >
                Learn More
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
};

const Tech = () => (
  <section className="pt-20 pb-4">
    <Container>
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
        Our Advisory Process
      </h2>
      <p className="text-gray-600 text-center max-w-2xl mx-auto mb-10">
        A structured approach combining financial expertise, technology, and execution excellence.
      </p>
      <div className="grid md:grid-cols-4 gap-6 text-center">
        {[
          {title: "Discovery" },
          {title: "Strategy" },
          {title: "Implementation" },
          {title: "Ongoing Support" }
        ].map((s, i) => (
          <div key={i} className="p-6 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
            <>
            <div className="inline-block px-2 py-1 text-xs font-semibold bg-slate-100 rounded-full mb-3">
              {s.num}
            </div>

            <h3 className="font-semibold text-lg">
              {s.title}
            </h3>
            </>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

const Leadership = () => {
  const [selectedMember, setSelectedMember] = useState(null);
  const isMobile = window.innerWidth < 768;
  const team = [
    { name: "Avish Minda", image: "/images/team/avi_pic.PNG", role: "Founder", 
      bio: "Avish Minda is a Chartered Accountant with experience across statutory audits, financial planning, IPO advisory, and business development, gained at a reputed Mumbai-based audit firm and a leading global professional services firm.He has worked on audits of large, diversified enterprises, with expertise in financial analysis, internal control evaluation, risk assessment, and IPO-related advisory, along with business development and strategic assignments.As Founder of Swastik Advisors, he combines technical depth with strategic insight and practical execution to deliver advisory solutions that balance compliance and growth, focused on long-term value creation."
    },
    { name: "Mayoor Kothari", image: "/images/team/mayur_pic.PNG", role: "Co-Founder",
      bio: "Mayoor Kothari is a Chartered Accountant with diversified experience in statutory audits, financial analysis, and business advisory across manufacturing, gaming, advertising, and event management.Through his experience at PwC, he has worked on statutory audits, internal control assessments, and financial reporting, with expertise in variance analysis, budgeting, forecasting, and performance monitoring to support data-driven decision-making.With a strong focus on CFO-led value creation, he helps emerging businesses build scalable finance functions—bringing a practical, analytical, and growth-oriented approach focused on long-term value and robust decision-making."
    },
    { name: "Sushil Ramchandani", image: "/images/team/sushil_pic.PNG", role: "Co-Founder",
      bio: "Sushil Ramchandani is a Chartered Accountant and Co-Founder of Swastik Advisors, with strong experience in statutory audits, financial reporting, and internal control reviews across consumer retail, construction, technology, and media. Prior to co-founding the firm, he gained valuable professional exposure at Deloitte, working on audit engagements for well-known companies, with expertise in risk assessment, Ind AS/IFRS compliance, and process improvement initiatives. Known for his practical approach and attention to detail, he emphasizes accuracy, governance, and robust financial systems—delivering reliable insights that help clients strengthen controls and make well-informed business decisions."
    },
    { name: "Shubham Lunkad", image: "/images/team/shubham_pic.JPG", role: "Co-Founder",
      bio: "Shubham is a Partner at Swastik Advisors, delivering end-to-end tax and regulatory advisory across domestic and international frameworks, including strategic tax planning, transaction structuring, and corporate reorganizations. His practice spans tax controversy management, cross-border taxation (DTAA, foreign tax credits, transfer pricing, global mobility), and US GAAP reporting with U.S. Federal and State tax expertise—supporting multinational enterprises and globally operating businesses. As a Certified Estate and Succession Planner, he also advises on wealth structuring and intergenerational succession, with an approach grounded in technical rigor, regulatory alignment, and commercially pragmatic outcomes."
    },
    { name: "Sanyam Mehta", image: "/images/team/sanyam_pic.JPG", role: "Co-Founder",
      bio: "Sanyam Mehta is a Semi-Qualified Chartered Accountant and Co-Founder of Swastik Advisors, with strong experience across audit, advisory, and financial services, including statutory and tax audits, financial reporting, and compliance across multiple industries. He has also been involved in IPO advisory engagements, including due diligence and financial analysis, and since 2023 has been independently associated as a freelancer delivering due diligence and advisory services for firms in Bengaluru. A tech-driven and hardworking professional with strong proficiency in financial tools and emerging technologies, he is known for his dedication, adaptability, and commitment to delivering high-quality, value-driven outcomes."  
    }
  ];

  return (
    <section className="pt-16 pb-20">
      <Container>
        <h2 className="text-4xl font-bold text-center mb-12">
          Leadership Team
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {team.map((member, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="text-center group cursor-pointer relative"
              onMouseEnter={() => setSelectedMember(member)}
              onMouseLeave={() => setSelectedMember(null)}
            >
              <div className="relative w-36 h-36 mx-auto mb-4 rounded-full overflow-hidden shadow-lg group cursor-pointer"
                onMouseEnter={() => setSelectedMember(member)}
                onMouseLeave={() => setSelectedMember(null)}
                onClick={() =>
                  isMobile &&
                  setSelectedMember(
                    selectedMember?.name === member.name ? null : member
                  )
                }
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h4 className="font-semibold text-gray-800">{member.name}</h4>
              <p className="text-sm text-gray-500 mt-1">{member.role}</p>
              {selectedMember?.name === member.name && (
                <div
                  className="fixed inset-0 pointer-events-none z-50">
                  <div
                    className="
                    absolute z-50
                    bg-white
                    rounded-2xl
                    shadow-2xl
                    p-6
                    w-[90vw]
                    max-w-xl
                    md:top-1/2
                    md:left-1/2
                    md:-translate-x-1/2
                    md:-translate-y-1/2
                    top-4
                    left-1/2
                    -translate-x-1/2
                    "
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img
                      src={selectedMember.image}
                      alt={selectedMember.name}
                      className="w-28 h-28 rounded-full mx-auto object-cover mb-4"
                    />

                    <h3 className="text-2xl font-bold text-center">
                      {selectedMember.name}
                    </h3>

                    <p className="text-gray-500 text-center mb-4">
                      {selectedMember.role}
                    </p>

                    <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                      {selectedMember.bio}
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
};

const BookConsultation = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error for this field when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const { email, phone } = formData;

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone validation: 10-15 digits, optionally starting with + or country code
    const cleanPhone = phone.replace(/[\s\-()]/g, "");
    const phoneRegex = /^\+?\d{10,15}$/;
    if (!phoneRegex.test(cleanPhone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const { name, company, email, phone, message } = formData;

    // WhatsApp message
    const whatsappText = `New Consultation Enquiry%0A%0A` +
      `*Name:* ${name}%0A` +
      `*Company:* ${company || "N/A"}%0A` +
      `*Email:* ${email}%0A` +
      `*Phone:* ${phone}%0A` +
      `*How can we help:* ${message}`;
    const whatsappUrl = `https://wa.me/919131438018?text=${whatsappText}`;
    window.open(whatsappUrl, "_blank");

    // Email via mailto
    const emailSubject = encodeURIComponent(`Consultation Enquiry from ${name}`);
    const emailBody = encodeURIComponent(
      `Name: ${name}\nCompany: ${company || "N/A"}\nEmail: ${email}\nPhone: ${phone}\n\nHow can we help:\n${message}`
    );
    const mailtoUrl = `mailto:contact@swastikadvisors.com?subject=${emailSubject}&body=${emailBody}`;
    window.location.href = mailtoUrl;

    setSubmitted(true);
    setErrors({});
    setTimeout(() => setSubmitted(false), 4000);
    setFormData({ name: "", company: "", email: "", phone: "", message: "" });
  };

  return (
    <section id="consultation" className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Container>
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Left side - Info */}
          <motion.div
            className="h-full flex flex-col justify-center pt-16"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div classname="pt-16">
            <h2 className="text-3xl font-bold mb-3 text-gray-900">
              Book a Consultation
            </h2>
            <p className="text-gray-600 mb-8 text-lg">
              Ready to transform your business? Fill out the form and our team
              will get back to you within 24 hours.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Email Us</p>
                  <p className="text-gray-500">contact@swastikadvisors.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Call Us</p>
                  <p className="text-gray-500">+91 9131438018</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Our Offices</p>
                  <p className="text-gray-500">Indore | Mumbai | Nagpur | Jalgaon | Bengaluru</p>
                </div>
              </div>
            </div>
            </div>
          </motion.div>

          {/* Right side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-xl p-8 space-y-5 border border-gray-100 w-full mt-8"
            >
              {submitted && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-50 border border-gray-200 text-gray-800 px-4 py-3 rounded-xl text-sm font-medium"
                >
                  ✓ Thank you! We'll get back to you shortly.
                </motion.div>
              )}

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Full Name <span className="text-red-400">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-500 focus:ring-2 focus:ring-gray-100 outline-none transition-all text-sm"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Company Name
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Enter your company name"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-500 focus:ring-2 focus:ring-gray-100 outline-none transition-all text-sm"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className={`w-full px-4 py-3 rounded-xl border ${errors.email ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : 'border-gray-200 focus:border-gray-500 focus:ring-gray-100'} focus:ring-2 outline-none transition-all text-sm`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Phone Number <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className={`w-full px-4 py-3 rounded-xl border ${errors.phone ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : 'border-gray-200 focus:border-gray-500 focus:ring-gray-100'} focus:ring-2 outline-none transition-all text-sm`}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
                  How Can We Help You? <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your requirements..."
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-500 focus:ring-2 focus:ring-gray-100 outline-none transition-all text-sm resize-none"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-black text-white py-3.5 rounded-xl font-semibold hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl active:scale-[0.98]"
              >
                Submit Enquiry
              </button>
            </form>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

const Contact = () => (
  <section id="contact" className="py-20 bg-black text-white text-center">
    <Container>
      <h2 className="text-3xl font-bold mb-4">
        Let’s Start Your Transformation Journey
      </h2>
      <p className="mb-4">contact@swastikadvisors.com</p>
      <p className="mb-4">+91 9131438018</p>
      <p>Indore | Mumbai | Nagpur | Jalgaon | Bengaluru</p>
    </Container>
  </section>
);

const HomePage = () => (
  <div className="font-sans">
    <Hero />
    <About />
    <Services />
    <Tech />
    <Leadership />
    <BookConsultation />
    <Contact />
  </div>
);

export default function App() {
  return (
    <BrowserRouter>
      <Navbar />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/services/:serviceId" element={<ServicesPage />} />
      </Routes>
    </BrowserRouter>
  );
}
